'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

// ── Types ──────────────────────────────────────────────────────
interface Product {
  id: number;
  badge: string;
  badgeClass: string;
  category: string;
  name: string;
  desc: string;
  price: string;
  priceNum: number;
  oldPrice: string;
  rating: number;
  reviews: number;
  bg: string;
  fg: string;
}

// ── Product Data ───────────────────────────────────────────────
const products: Product[] = [
  { id:1,  badge:'Best Seller', badgeClass:'best',     category:'Inkjet',   name:'HP DeskJet 2331',              desc:'Compact all-in-one for everyday home printing. Print, scan, and copy with ease.',                                    price:'$99',  priceNum:99,  oldPrice:'$119', rating:5, reviews:248, bg:'E8EEFF', fg:'005CBB' },
  { id:2,  badge:'New',         badgeClass:'new',      category:'Wireless', name:'HP DeskJet 4826',              desc:'Smart wireless all-in-one with HP+ smart printing system and Instant Ink ready.',                                    price:'$129', priceNum:129, oldPrice:'$159', rating:5, reviews:134, bg:'D8E8FF', fg:'1E3A6E' },
  { id:3,  badge:'Sale',        badgeClass:'sale',     category:'Laser',    name:'HP LaserJet MFP M141w',        desc:'Ultra-compact wireless monochrome laser — perfect for small offices and WFH setups.',                                 price:'$174', priceNum:174, oldPrice:'$215', rating:5, reviews:312, bg:'F0F0F0', fg:'2D2D2D' },
  { id:4,  badge:'Best Seller', badgeClass:'best',     category:'Office',   name:'HP OfficeJet Pro 9010e',       desc:'Professional-grade all-in-one for high-volume office printing with business features.',                               price:'$229', priceNum:229, oldPrice:'$279', rating:4, reviews:189, bg:'E0EAFF', fg:'003087' },
  { id:5,  badge:'',            badgeClass:'',         category:'Photo',    name:'HP ENVY 6032e',                desc:'Vibrant borderless photo printing at home with automatic 2-sided printing.',                                           price:'$149', priceNum:149, oldPrice:'$179', rating:5, reviews:97,  bg:'FFE8F5', fg:'8B0057' },
  { id:6,  badge:'New',         badgeClass:'new',      category:'Laser',    name:'HP Color LaserJet Pro M183fw', desc:'Colour laser MFP built for office efficiency — fast, sharp, and network-ready.',                                      price:'$399', priceNum:399, oldPrice:'$469', rating:4, reviews:76,  bg:'EDE8FF', fg:'3D0099' },
  { id:7,  badge:'',            badgeClass:'',         category:'Home',     name:'HP Ink Tank 315',              desc:'Low cost-per-page inkjet with cartridge-free tank system. Great for heavy users.',                                     price:'$119', priceNum:119, oldPrice:'$147', rating:4, reviews:421, bg:'E0F7EF', fg:'006D45' },
  { id:8,  badge:'Best Seller', badgeClass:'best',     category:'Wireless', name:'HP Smart Tank 589',            desc:'Wireless ink tank with huge capacity — ideal for homes and small teams printing daily.',                                price:'$199', priceNum:199, oldPrice:'$239', rating:5, reviews:267, bg:'E0EEFF', fg:'004D99' },
  { id:9,  badge:'',            badgeClass:'',         category:'Laser',    name:'HP LaserJet Pro M404dn',       desc:'High-speed monochrome laser for busy offices demanding reliability and speed.',                                        price:'$299', priceNum:299, oldPrice:'$359', rating:5, reviews:143, bg:'F5F5F5', fg:'1A1A1A' },
  { id:10, badge:'Sale',        badgeClass:'sale',     category:'Office',   name:'HP LaserJet MFP M236sdw',      desc:'Wireless monochrome laser MFP with built-in security features for professional offices.',                              price:'$259', priceNum:259, oldPrice:'$309', rating:4, reviews:88,  bg:'EEEEF5', fg:'333344' },
  { id:11, badge:'New',         badgeClass:'new',      category:'Photo',    name:'HP Sprocket Plus',             desc:'Pocket-sized photo printer that connects to your phone. Print 2×3 zink photos instantly.',                             price:'$65',  priceNum:65,  oldPrice:'$84',  rating:5, reviews:312, bg:'FFE0F0', fg:'CC1A7A' },
  { id:12, badge:'',            badgeClass:'',         category:'Home',     name:'HP DeskJet Ink Advantage 5276',desc:'Budget-friendly wireless all-in-one with Ink Advantage for lower running costs.',                                      price:'$96',  priceNum:96,  oldPrice:'$114', rating:4, reviews:176, bg:'EAF2FF', fg:'005CBB' },
];

const filterCategories = ['All Printers', 'Inkjet', 'Laser', 'Wireless', 'Office', 'Photo', 'Home'];

const badgeColors: Record<string, string> = {
  best: '#005CBB',
  new:  '#00A86B',
  sale: '#FF6B00',
};

const brandPlaceholders: Record<string, string> = {
  HP:      'e.g., HP DeskJet 2700',
  Canon:   'e.g., Canon PIXMA MG3620',
  Brother: 'e.g., Brother MFC-L2710DW',
  Epson:   'e.g., Epson EcoTank ET-2800',
};

// ── SVG printer image generator ───────────────────────────────
function makeSVG(bg: string, fg: string, label: string, sub: string): string {
  const w = 160, h = 140;
  const cx = w / 2;
  const cy = h * 0.42;
  const pw = w * 0.72, ph = h * 0.38, pr = 8;
  const px = cx - pw / 2, py = cy - ph / 2;
  const slotW = pw * 0.55, slotH = ph * 0.1;
  const slotX = cx - slotW / 2, slotY = py + ph * 0.62;
  const paperW = pw * 0.45, paperH = ph * 0.28;
  const paperX = cx - paperW / 2, paperY = py - paperH * 0.6;
  const trayW = pw * 0.6, trayH = ph * 0.18;
  const trayX = cx - trayW / 2, trayY = py + ph * 0.78;
  const words = label.split(' ');
  const mid = Math.ceil(words.length / 2);
  const line1 = words.slice(0, mid).join(' ');
  const line2 = words.slice(mid).join(' ');
  const fs = 11;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
    <rect width="${w}" height="${h}" fill="#${bg}" rx="10"/>
    <ellipse cx="${cx}" cy="${py + ph + 6}" rx="${pw * 0.4}" ry="5" fill="rgba(0,0,0,0.1)"/>
    <rect x="${paperX}" y="${paperY}" width="${paperW}" height="${paperH}" fill="white" rx="3" stroke="#${fg}" stroke-width="1" stroke-opacity="0.3"/>
    <line x1="${paperX + paperW * 0.15}" y1="${paperY + paperH * 0.3}" x2="${paperX + paperW * 0.85}" y2="${paperY + paperH * 0.3}" stroke="#${fg}" stroke-width="1" stroke-opacity="0.25"/>
    <line x1="${paperX + paperW * 0.15}" y1="${paperY + paperH * 0.5}" x2="${paperX + paperW * 0.85}" y2="${paperY + paperH * 0.5}" stroke="#${fg}" stroke-width="1" stroke-opacity="0.25"/>
    <line x1="${paperX + paperW * 0.15}" y1="${paperY + paperH * 0.7}" x2="${paperX + paperW * 0.7}" y2="${paperY + paperH * 0.7}" stroke="#${fg}" stroke-width="1" stroke-opacity="0.25"/>
    <rect x="${px}" y="${py}" width="${pw}" height="${ph}" fill="#${fg}" rx="${pr}" opacity="0.92"/>
    <rect x="${px + 2}" y="${py + 2}" width="${pw - 4}" height="${ph * 0.18}" fill="white" rx="${pr - 2}" opacity="0.12"/>
    <rect x="${slotX}" y="${slotY}" width="${slotW}" height="${slotH}" fill="rgba(0,0,0,0.25)" rx="2"/>
    <circle cx="${px + pw * 0.78}" cy="${py + ph * 0.28}" r="5" fill="rgba(255,255,255,0.55)"/>
    <circle cx="${px + pw * 0.88}" cy="${py + ph * 0.28}" r="5" fill="#${bg}" opacity="0.9"/>
    <rect x="${trayX}" y="${trayY}" width="${trayW}" height="${trayH}" fill="#${fg}" rx="4" opacity="0.7"/>
    <text x="${cx}" y="${py + ph + trayH + 18}" text-anchor="middle" font-family="Arial,sans-serif" font-weight="700" font-size="${fs}" fill="#${fg}">${line1}</text>
    ${line2 ? `<text x="${cx}" y="${py + ph + trayH + 18 + fs + 3}" text-anchor="middle" font-family="Arial,sans-serif" font-weight="700" font-size="${fs}" fill="#${fg}">${line2}</text>` : ''}
    ${sub ? `<text x="${cx}" y="${h - 6}" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" fill="#${fg}" opacity="0.6">${sub}</text>` : ''}
  </svg>`;
  return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
}

// ── Brand Logo SVGs ────────────────────────────────────────────
const BrandLogos: Record<string, React.ReactNode> = {
  HP: (
    <svg viewBox="0 0 100 100" width="100" height="100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="hpSphere" cx="38%" cy="32%" r="62%">
          <stop offset="0%" stopColor="#5ec8f0" />
          <stop offset="45%" stopColor="#1a8fd1" />
          <stop offset="100%" stopColor="#004f8c" />
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#hpSphere)" />
      <text x="50" y="64" textAnchor="middle" fontFamily="Arial,Helvetica,sans-serif" fontStyle="italic" fontWeight="900" fontSize="40" fill="#fff" letterSpacing="-2">hp</text>
    </svg>
  ),
  Canon: (
    <svg viewBox="0 0 200 80" width="180" height="72" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="canonGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e00000" />
          <stop offset="100%" stopColor="#aa0000" />
        </linearGradient>
      </defs>
      <text x="100" y="62" textAnchor="middle" fontFamily="Georgia,'Times New Roman',serif" fontStyle="italic" fontWeight="700" fontSize="58" fill="url(#canonGrad)">Canon</text>
    </svg>
  ),
  Brother: (
    <svg viewBox="0 0 220 80" width="200" height="72" xmlns="http://www.w3.org/2000/svg">
      <text x="110" y="52" textAnchor="middle" fontFamily="Arial,Helvetica,sans-serif" fontWeight="900" fontSize="38" fill="#0057a8" letterSpacing="-1">brother</text>
      <text x="196" y="26" fontFamily="Arial,sans-serif" fontSize="14" fill="#0057a8">®</text>
      <text x="110" y="68" textAnchor="middle" fontFamily="Arial,Helvetica,sans-serif" fontSize="9.5" fill="#888" letterSpacing="2">SINCE 1908</text>
    </svg>
  ),
  Epson: (
    <svg viewBox="0 0 200 70" width="180" height="64" xmlns="http://www.w3.org/2000/svg">
      <text x="100" y="50" textAnchor="middle" fontFamily="Arial,Helvetica,sans-serif" fontWeight="900" fontSize="44" fill="#003087" letterSpacing="3">EPSON</text>
      <text x="188" y="22" fontFamily="Arial,sans-serif" fontSize="14" fill="#003087">®</text>
    </svg>
  ),
};

const brandNames = ['HP', 'Canon', 'Brother', 'Epson'];

// ── Modal 1 Logo SVGs (inline for modal display) ───────────────
const ModalLogos: Record<string, React.ReactNode> = {
  HP: (
    <svg viewBox="0 0 100 100" width="72" height="72" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="hpM" cx="38%" cy="32%" r="62%">
          <stop offset="0%" stopColor="#5ec8f0" />
          <stop offset="45%" stopColor="#1a8fd1" />
          <stop offset="100%" stopColor="#004f8c" />
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill="url(#hpM)" />
      <text x="50" y="64" textAnchor="middle" fontFamily="Arial,Helvetica,sans-serif" fontStyle="italic" fontWeight="900" fontSize="40" fill="#fff" letterSpacing="-2">hp</text>
    </svg>
  ),
  Canon: (
    <svg viewBox="0 0 200 80" width="160" height="64" xmlns="http://www.w3.org/2000/svg">
      <text x="100" y="62" textAnchor="middle" fontFamily="Georgia,'Times New Roman',serif" fontStyle="italic" fontWeight="700" fontSize="58" fill="#e00000">Canon</text>
    </svg>
  ),
  Brother: (
    <svg viewBox="0 0 220 80" width="190" height="64" xmlns="http://www.w3.org/2000/svg">
      <text x="110" y="52" textAnchor="middle" fontFamily="Arial,Helvetica,sans-serif" fontWeight="900" fontSize="38" fill="#0057a8" letterSpacing="-1">brother</text>
      <text x="196" y="26" fontFamily="Arial,sans-serif" fontSize="14" fill="#0057a8">®</text>
      <text x="110" y="68" textAnchor="middle" fontFamily="Arial,Helvetica,sans-serif" fontSize="9.5" fill="#888" letterSpacing="2">SINCE 1908</text>
    </svg>
  ),
  Epson: (
    <svg viewBox="0 0 200 70" width="170" height="56" xmlns="http://www.w3.org/2000/svg">
      <text x="100" y="50" textAnchor="middle" fontFamily="Arial,Helvetica,sans-serif" fontWeight="900" fontSize="44" fill="#003087" letterSpacing="3">EPSON</text>
      <text x="188" y="22" fontFamily="Arial,sans-serif" fontSize="14" fill="#003087">®</text>
    </svg>
  ),
};

// ── Main Component ─────────────────────────────────────────────
export default function PrintPage() {
  // Filter state
  const [activeFilters, setActiveFilters] = useState<string[]>(['All Printers']);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('default');
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(500);
  const [minRating, setMinRating] = useState(0);
  const [onSale, setOnSale] = useState(false);
  const [newArrivals, setNewArrivals] = useState(false);
  const [bestSellers, setBestSellers] = useState(false);

  // Cart state
  const [cartCount, setCartCount] = useState(0);
  const [toast, setToast] = useState<{ name: string; price: string } | null>(null);
  const [addedIds, setAddedIds] = useState<Record<number, boolean>>({});
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Modal state
  const [modal, setModal] = useState<'none' | 'modal1' | 'modal2' | 'modal3'>('none');
  const [currentBrand, setCurrentBrand] = useState('HP');
  const [printerModel, setPrinterModel] = useState('');
  const [modelDisplay, setModelDisplay] = useState('');
  const [dlPct, setDlPct] = useState(0);
  const [instPct, setInstPct] = useState(0);
  const [dlDone, setDlDone] = useState(false);
  const [instFailed, setInstFailed] = useState(false);
  const [instStarted, setInstStarted] = useState(false);
  const dlTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const instTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Product images (generated client-side to avoid hydration mismatch)
  const [productImages, setProductImages] = useState<Record<number, string>>({});

  useEffect(() => {
    const imgs: Record<number, string> = {};
    products.forEach((p) => {
      imgs[p.id] = makeSVG(p.bg, p.fg, p.name, p.category);
    });
    setProductImages(imgs);
  }, []);

  // ── Filter logic ─────────────────────────────────────────────
  const toggleFilter = (cat: string) => {
    if (cat === 'All Printers') {
      setActiveFilters(['All Printers']);
    } else {
      const without = activeFilters.filter((f) => f !== 'All Printers' && f !== cat);
      const newFilters = activeFilters.includes(cat) ? without : [...without, cat];
      setActiveFilters(newFilters.length === 0 ? ['All Printers'] : newFilters);
    }
  };

  const filtered = products
    .filter((p) => {
      if (!activeFilters.includes('All Printers') && !activeFilters.includes(p.category)) return false;
      if (p.priceNum < minPrice || p.priceNum > maxPrice) return false;
      if (minRating > 0 && p.rating < minRating) return false;
      if (onSale && p.badge !== 'Sale') return false;
      if (newArrivals && p.badge !== 'New') return false;
      if (bestSellers && p.badge !== 'Best Seller') return false;
      if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.desc.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      if (sort === 'price-asc') return a.priceNum - b.priceNum;
      if (sort === 'price-desc') return b.priceNum - a.priceNum;
      if (sort === 'rating') return b.rating - a.rating || b.reviews - a.reviews;
      return 0;
    });

  // ── Cart ──────────────────────────────────────────────────────
  const addToCart = (product: Product) => {
    setCartCount((c) => c + 1);
    setAddedIds((prev) => ({ ...prev, [product.id]: true }));
    setToast({ name: product.name, price: product.price });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      setAddedIds((prev) => { const n = { ...prev }; delete n[product.id]; return n; });
    }, 3000);
  };

  // ── Modal helpers ─────────────────────────────────────────────
  const clearTimers = () => {
    if (dlTimerRef.current) clearInterval(dlTimerRef.current);
    if (instTimerRef.current) clearInterval(instTimerRef.current);
  };

  const closeAll = () => {
    clearTimers();
    setModal('none');
    setDlPct(0);
    setInstPct(0);
    setDlDone(false);
    setInstFailed(false);
    setInstStarted(false);
    setPrinterModel('');
  };

  const openModal1 = (brand: string) => {
    closeAll();
    setCurrentBrand(brand);
    setPrinterModel('');
    setModal('modal1');
  };

  const startDownload = () => {
    const model = printerModel.trim() || currentBrand + ' Printer';
    setModelDisplay(model);
    setModal('modal2');
    setDlPct(0);
    setInstPct(0);
    setDlDone(false);
    setInstFailed(false);
    setInstStarted(false);

    let pct = 0;
    clearTimers();
    dlTimerRef.current = setInterval(() => {
      pct++;
      setDlPct(pct);
      if (pct >= 100) {
        clearInterval(dlTimerRef.current!);
        setDlDone(true);
        setTimeout(() => runInstall(), 700);
      }
    }, 50);
  };

  const runInstall = () => {
    setInstStarted(true);
    let pct = 0;
    instTimerRef.current = setInterval(() => {
      pct++;
      setInstPct(pct);
      if (pct >= 89) {
        clearInterval(instTimerRef.current!);
        setInstFailed(true);
        setTimeout(() => {
          setModal('modal3');
        }, 800);
      }
    }, 55);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimers();
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Poppins:wght@300;400;500;600;700;800&family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

        :root {
          --blue: #005CBB;
          --blue-dark: #003D7A;
          --blue-light: #E8F1FF;
          --accent: #FF6B00;
          --dark: #0A0F1E;
          --mid: #1E2A45;
          --text: #1A2235;
          --muted: #6B7A99;
          --border: #E2E8F4;
        }

        /* ── Brand cards ── */
        .shop-brand-card {
          background: #fff;
          border-radius: 20px;
          padding: 32px 24px 28px;
          text-align: center;
          box-shadow: 0 6px 32px rgba(0,0,0,0.18);
          cursor: pointer;
          transition: transform .22s, box-shadow .22s;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          min-height: 220px;
        }
        .shop-brand-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 18px 48px rgba(0,0,0,0.26);
        }
        .brand-logo-wrap {
          height: 100px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          overflow: visible;
          flex-shrink: 0;
        }
        .brand-label {
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          font-size: 16px;
          color: #1a1a1a;
          margin: 14px 0 18px;
          line-height: 1;
        }

        /* ── Start Now button ── */
        @keyframes btnPulse {
          0%,100% { transform: scale(1); box-shadow: 0 4px 18px rgba(26,143,255,0.45); }
          50%      { transform: scale(1.08); box-shadow: 0 8px 28px rgba(26,143,255,0.65); }
        }
        .shop-start-btn {
          background: #1a8fff;
          color: #fff;
          border-radius: 50px;
          padding: 11px 36px;
          font-size: 15px;
          font-weight: 700;
          font-family: 'Poppins', sans-serif;
          text-decoration: none;
          display: inline-block;
          letter-spacing: .2px;
          animation: btnPulse 3.2s ease-in-out infinite;
          transition: background .18s, transform .18s;
          white-space: nowrap;
          border: none;
          cursor: pointer;
        }
        .shop-start-btn:hover {
          background: #0060d0;
          animation: none;
          transform: scale(1.08);
          box-shadow: 0 10px 30px rgba(26,143,255,0.6);
        }

        /* ── Modal overlay ── */
        .dv-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.55);
          z-index: 9000;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(3px);
        }
        .dv-overlay.open { display: flex; }
        .dv-modal {
          background: #fff;
          border-radius: 16px;
          padding: 40px 40px 36px;
          width: 100%;
          max-width: 560px;
          position: relative;
          box-shadow: 0 24px 80px rgba(0,0,0,0.22);
          animation: modalIn .25s cubic-bezier(.34,1.56,.64,1);
        }
        @keyframes modalIn {
          from { opacity: 0; transform: scale(.88) translateY(20px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .dv-title {
          font-family: 'Bebas Neue', sans-serif;
          font-weight: 400;
          font-size: 2rem;
          color: #1a8fff;
          text-align: center;
          margin-bottom: 28px;
          letter-spacing: 2.5px;
          line-height: 1.2;
          word-spacing: 4px;
        }
        .dv-label {
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          font-size: 14px;
          color: #1a1a1a;
          margin-bottom: 8px;
          display: block;
        }
        .dv-input {
          width: 100%;
          border: 1.5px solid #d0d8e8;
          border-radius: 8px;
          padding: 13px 16px;
          font-size: 15px;
          font-family: 'Poppins', sans-serif;
          color: #333;
          outline: none;
          transition: border-color .2s;
          margin-bottom: 24px;
          box-sizing: border-box;
        }
        .dv-input:focus { border-color: #1a8fff; }
        .dv-dl-btn {
          width: 100%;
          background: #1a8fff;
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 16px;
          font-family: 'Poppins', sans-serif;
          font-weight: 800;
          font-size: 16px;
          cursor: pointer;
          transition: background .2s, transform .15s;
        }
        .dv-dl-btn:hover { background: #0070e0; transform: translateY(-1px); }

        /* Progress bars */
        .dv-model-line { font-family: 'Poppins', sans-serif; font-size: 14px; color: #333; margin-bottom: 22px; }
        .dv-bar-section { margin-bottom: 18px; }
        .dv-bar-section.disabled .dv-progress-label { color: #b0b8c8; }
        .dv-bar-section.disabled .dv-progress-track { background: #f0f2f5; }
        .dv-progress-label { font-family: 'Poppins', sans-serif; font-weight: 700; font-size: 14px; color: #333; margin-bottom: 10px; }
        .dv-progress-track { background: #e0e8f0; border-radius: 50px; height: 34px; overflow: hidden; position: relative; }
        .dv-progress-bar {
          height: 100%; border-radius: 50px;
          background: linear-gradient(90deg, #1a8fff, #00c2ff);
          transition: width .35s ease;
          display: flex; align-items: center; justify-content: center;
          min-width: 44px;
        }
        .dv-install-bar {
          height: 100%; border-radius: 50px;
          background: linear-gradient(90deg, #00b050, #00d966);
          transition: width .35s ease;
          display: flex; align-items: center; justify-content: center;
        }
        .dv-install-bar.error { background: linear-gradient(90deg, #e53935, #ff6b6b); }
        .dv-progress-pct { font-family: 'Poppins', sans-serif; font-weight: 800; font-size: 13px; color: #fff; }

        /* Modal 3 */
        .dv-error-title { font-family: 'Bebas Neue', sans-serif; font-weight: 800; font-size: 1.5rem; color: #cc0000; text-align: center; margin-bottom: 14px; }
        .dv-error-desc { font-family: 'Poppins', sans-serif; font-size: 14.5px; color: #333; text-align: center; line-height: 1.75; margin-bottom: 22px; }

        /* Product cards */
        .product-card {
          background: #fff;
          border-radius: 20px;
          overflow: hidden;
          border: 1px solid var(--border);
          transition: transform .2s, box-shadow .2s;
          cursor: pointer;
        }
        .product-card:hover { transform: translateY(-6px); box-shadow: 0 20px 48px rgba(0,30,80,.12); }
        .product-img-wrap {
          background: linear-gradient(135deg, #F0F4FF, #E8EEFF);
          height: 200px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          padding: 1rem;
        }
        .product-img { width: 160px; height: 140px; object-fit: contain; transition: transform .3s; }
        .product-card:hover .product-img { transform: scale(1.05); }

        /* Why cards */
        .why-card {
          background: rgba(255,255,255,.05);
          border: 1px solid rgba(255,255,255,.1);
          border-radius: 16px;
          padding: 1.5rem;
          transition: background .2s, border-color .2s;
        }
        .why-card:hover { background: rgba(0,92,187,.18); border-color: rgba(0,92,187,.4); }

        /* Sidebar */
        .filter-sidebar {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 1.5rem;
        }
        .sidebar-section { border-top: 1px solid var(--border); padding-top: 16px; margin-top: 8px; }

        /* Shop layout */
        .shop-layout { display: grid; grid-template-columns: 260px 1fr; gap: 2rem; }
        .products-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 1.5rem; }

        /* PSCF section */
        .pscf2-section { background: #fff; padding: 80px 2rem; }
        .pscf2-inner { max-width: 1140px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 5rem; align-items: center; }
        .pscf2-left { position: relative; height: 400px; min-width: 0; }
        .pscf2-img-main { position: absolute; left: 52px; top: 0; width: 82%; height: 330px; border-radius: 22px; overflow: hidden; box-shadow: 0 14px 52px rgba(0,0,0,0.14); }
        .pscf2-img-small { position: absolute; right: 0; bottom: 0; width: 54%; height: 210px; border-radius: 22px; overflow: hidden; box-shadow: 0 16px 44px rgba(0,0,0,0.18); border: 4px solid #fff; z-index: 2; }
        .pscf2-badge { position: absolute; left: 22px; top: 18px; background: #1a8fff; border-radius: 16px; padding: 14px 20px; z-index: 10; min-width: 126px; text-align: center; box-shadow: 0 6px 28px rgba(26,143,255,0.42); }
        .pscf2-badge-num { font-family: 'Bebas Neue', sans-serif; font-weight: 800; font-size: 34px; color: #fff; line-height: 1; }
        .pscf2-badge-label { font-family: 'Poppins', sans-serif; font-size: 11.5px; font-weight: 500; color: rgba(255,255,255,0.93); margin-top: 4px; line-height: 1.35; }
        .pscf2-headline { font-family: 'Bebas Neue', sans-serif; font-weight: 800; font-size: clamp(1.7rem,2.8vw,2.45rem); color: #1a2235; line-height: 1.18; margin-bottom: 13px; }
        .pscf2-sub { font-size: 15.5px; color: #6b7a99; margin-bottom: 26px; line-height: 1.6; font-family: 'Poppins', sans-serif; }
        .pscf2-list { list-style: none; padding: 0; margin: 0 0 34px; display: flex; flex-direction: column; gap: 13px; }
        .pscf2-list li { display: flex; align-items: center; gap: 12px; font-size: 15px; color: #3a4a6a; font-family: 'Poppins', sans-serif; }
        .pscf2-dot { width: 7px; height: 7px; background: #1a8fff; border-radius: 50%; flex-shrink: 0; }
        .pscf2-btn { background: #1a8fff; color: #fff; padding: 13px 34px; border-radius: 50px; font-family: 'Poppins', sans-serif; font-weight: 700; font-size: 15px; text-decoration: none; display: inline-block; transition: background .2s, transform .15s; }
        .pscf2-btn:hover { background: #0070e0; transform: translateY(-2px); }

        /* Toast */
        .sc-toast {
          position: fixed; bottom: 28px; right: 28px; z-index: 99999;
          background: #0A0F1E; color: #fff; border-radius: 14px;
          padding: 14px 20px; display: flex; align-items: center; gap: 12px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.35);
          font-family: 'DM Sans', sans-serif; font-size: 14px;
          min-width: 280px; max-width: 340px;
          border: 1px solid rgba(255,255,255,0.1);
          animation: toastIn .3s cubic-bezier(.34,1.56,.64,1);
        }
        @keyframes toastIn {
          from { transform: translateY(100px); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }

        @media (max-width: 900px) {
          .shop-layout { grid-template-columns: 1fr; }
          .pscf2-inner { grid-template-columns: 1fr !important; gap: 2rem; }
          .pscf2-left { height: 320px; margin-bottom: 1rem; }
          .pscf2-img-main { left: 30px; width: 80%; height: 260px; }
          .pscf2-img-small { width: 52%; height: 170px; }
        }
        @media (max-width: 600px) {
          .brand-grid { grid-template-columns: repeat(2,1fr) !important; }
          .products-grid { grid-template-columns: 1fr 1fr; }
        }
      `}</style>

      <div style={{ background: '#F7F9FF' }}>
        <Header cartCount={cartCount} />

        {/* ── HERO ── */}
        <section style={{ position: 'relative', minHeight: 540, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: '#18191c', zIndex: 0 }} />

          {/* Person silhouette left */}
          <svg style={{ position: 'absolute', left: 0, bottom: 0, height: '100%', width: 340, zIndex: 1, opacity: 0.28 }} viewBox="0 0 340 540" preserveAspectRatio="xMinYMax meet">
            <ellipse cx="155" cy="380" rx="85" ry="180" fill="#2a2a2e" />
            <ellipse cx="155" cy="195" rx="44" ry="52" fill="#3a2e28" />
            <rect x="138" y="240" width="34" height="30" fill="#3a2e28" />
            <polygon points="155,268 135,360 175,360" fill="#1a1a1a" />
            <polygon points="155,268 149,310 155,330 161,310" fill="#2255aa" />
            <path d="M100,320 Q60,380 30,420" stroke="#3a2e28" strokeWidth="32" fill="none" strokeLinecap="round" />
            <path d="M210,320 Q240,370 260,400" stroke="#2a2a2e" strokeWidth="28" fill="none" strokeLinecap="round" />
            <rect x="20" y="420" width="200" height="12" rx="4" fill="#333" />
            <rect x="10" y="430" width="220" height="8" rx="3" fill="#222" />
          </svg>

          {/* Printer silhouette right */}
          <svg style={{ position: 'absolute', right: 0, bottom: 0, height: '85%', width: 280, zIndex: 1, opacity: 0.30 }} viewBox="0 0 280 460" preserveAspectRatio="xMaxYMax meet">
            <rect x="40" y="120" width="200" height="130" rx="12" fill="#2d2d30" />
            <rect x="40" y="120" width="200" height="28" rx="12" fill="#333" />
            <rect x="80" y="200" width="120" height="12" rx="4" fill="#1a1a1a" />
            <rect x="95" y="88" width="90" height="38" rx="3" fill="#e8e8e8" opacity="0.6" />
            <circle cx="210" cy="148" r="6" fill="#1a8fff" opacity="0.8" />
            <circle cx="228" cy="148" r="6" fill="#22c55e" opacity="0.6" />
            <rect x="60" y="248" width="160" height="22" rx="5" fill="#222" />
            <rect x="48" y="133" width="60" height="36" rx="4" fill="#1a3a5c" />
            <rect x="52" y="137" width="52" height="28" rx="3" fill="#0d2235" />
            <rect x="100" y="270" width="80" height="120" rx="4" fill="#222" />
            <rect x="70" y="385" width="140" height="14" rx="6" fill="#1a1a1a" />
          </svg>

          {/* Overlay */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right,rgba(18,19,28,.45) 0%,rgba(18,19,28,.10) 35%,rgba(18,19,28,.10) 65%,rgba(18,19,28,.45) 100%)', zIndex: 3, pointerEvents: 'none' }} />

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 10, textAlign: 'center', padding: '72px 2rem 60px', width: '100%', maxWidth: 1000, margin: '0 auto' }}>
            <p style={{ fontFamily: "'Poppins',sans-serif", fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.78)', marginBottom: 14, letterSpacing: '2px', textTransform: 'uppercase' }}>Connect Your Printer</p>
            <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontWeight: 400, fontSize: 'clamp(2.8rem,6vw,4.8rem)', color: '#fff', lineHeight: 1.05, marginBottom: 14, letterSpacing: '3px' }}>Download Your Printer Drivers</h1>
            <p style={{ fontFamily: "'Poppins',sans-serif", fontSize: 15, color: 'rgba(255,255,255,0.60)', marginBottom: 44, fontWeight: 300 }}>Select Your Printer Model</p>

            {/* Brand Cards */}
            <div className="brand-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 24, maxWidth: 960, margin: '0 auto 40px' }}>
              {brandNames.map((brand) => (
                <div key={brand} className="shop-brand-card">
                  <div className="brand-logo-wrap">
                    {BrandLogos[brand]}
                  </div>
                  <div className="brand-label">Printer Setup</div>
                  <button
                    className="shop-start-btn"
                    onClick={(e) => { e.preventDefault(); openModal1(brand); }}
                  >
                    Start Now
                  </button>
                </div>
              ))}
            </div>

            {/* Trust badges */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 32, flexWrap: 'wrap' }}>
              {['Official Drivers', 'Secure Download', 'Official Download'].map((item) => (
                <span key={item} style={{ fontFamily: "'Poppins',sans-serif", fontSize: 13.5, color: 'rgba(255,255,255,0.68)', display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ color: '#22c55e', fontWeight: 800, fontSize: 15 }}>✓</span> {item}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── PRINT | SCAN | COPY | FAX ── */}
        <section className="pscf2-section">
          <div className="pscf2-inner">
            <div className="pscf2-left">
              <div className="pscf2-img-main">
                <svg width="100%" height="100%" viewBox="0 0 440 330" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
                  <defs>
                    <linearGradient id="wg1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#cbc5bc" /><stop offset="100%" stopColor="#b8b2a8" /></linearGradient>
                    <linearGradient id="fg1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#d4cfc8" /><stop offset="100%" stopColor="#c2bdb5" /></linearGradient>
                  </defs>
                  <rect width="440" height="330" fill="url(#wg1)" />
                  <rect y="240" width="440" height="90" fill="url(#fg1)" />
                  <rect x="320" y="12" width="88" height="105" rx="5" fill="#fafafa" opacity="0.92" />
                  <rect x="320" y="12" width="88" height="26" rx="5" fill="#e53935" opacity="0.88" />
                  <text x="364" y="30" textAnchor="middle" fontFamily="Arial,sans-serif" fontSize="11" fontWeight="700" fill="#fff">MARCH</text>
                  <rect x="65" y="188" width="256" height="118" rx="12" fill="#c8c5c0" />
                  <rect x="65" y="188" width="256" height="32" rx="12" fill="#b8b5b0" />
                  <rect x="74" y="222" width="96" height="60" rx="6" fill="#1a2d45" />
                  <rect x="78" y="226" width="88" height="52" rx="4" fill="#0d1e30" />
                  <line x1="83" y1="237" x2="157" y2="237" stroke="#1a8fff" strokeWidth="1.8" opacity="0.85" />
                  <line x1="83" y1="248" x2="148" y2="248" stroke="#1a8fff" strokeWidth="1.2" opacity="0.55" />
                  <rect x="130" y="148" width="130" height="58" rx="10" fill="#3a3a3a" />
                  <rect x="140" y="156" width="110" height="42" rx="7" fill="#222" />
                  <rect x="150" y="163" width="70" height="20" rx="3" fill="#1a8fff" opacity="0.7" />
                  <text x="185" y="177" textAnchor="middle" fontFamily="Arial,sans-serif" fontSize="9" fontWeight="700" fill="#fff">HP TONER</text>
                </svg>
              </div>
              <div className="pscf2-img-small">
                <svg width="100%" height="100%" viewBox="0 0 260 215" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
                  <defs><linearGradient id="bg2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f2ede6" /><stop offset="100%" stopColor="#e8e2da" /></linearGradient></defs>
                  <rect width="260" height="215" fill="url(#bg2)" />
                  <ellipse cx="130" cy="72" rx="52" ry="42" fill="#111" />
                  <ellipse cx="130" cy="112" rx="40" ry="46" fill="#d4a882" />
                  <rect x="118" y="152" width="24" height="26" rx="5" fill="#c99c78" />
                  <ellipse cx="115" cy="110" rx="7" ry="4.5" fill="#1a1a1a" />
                  <ellipse cx="145" cy="110" rx="7" ry="4.5" fill="#1a1a1a" />
                  <path d="M119 134 Q130 140 141 134" stroke="#b87060" strokeWidth="2" fill="none" strokeLinecap="round" />
                </svg>
              </div>
              <div className="pscf2-badge">
                <div className="pscf2-badge-num">12</div>
                <div className="pscf2-badge-label">Years of Excellence</div>
              </div>
            </div>
            <div>
              <h2 className="pscf2-headline">Print | Scan | Copy | Fax</h2>
              <p className="pscf2-sub">Your Trusted Printer Setup Companion</p>
              <ul className="pscf2-list">
                <li><span className="pscf2-dot" />Fast &amp; Reliable Driver Downloads</li>
                <li><span className="pscf2-dot" />Step-by-Step Wireless Printer Setup</li>
                <li><span className="pscf2-dot" />Compatible With All Major OS Versions</li>
              </ul>
              <Link href="/shop" className="pscf2-btn">Setup Your Printer →</Link>
            </div>
          </div>
        </section>

        {/* ── SHOP GRID ── */}
        <div style={{ padding: '48px 2rem', background: '#F0F4FF' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto' }} className="shop-layout">

            {/* Sidebar */}
            <aside className="filter-sidebar" style={{ height: 'fit-content', position: 'sticky', top: 80 }}>
              <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 14, color: '#1A2235', marginBottom: 16 }}>🔍 Filter by Category</div>
              {filterCategories.map((cat) => (
                <label key={cat} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={activeFilters.includes(cat)}
                    onChange={() => toggleFilter(cat)}
                    style={{ accentColor: '#005CBB', width: 15, height: 15 }}
                  />
                  <span style={{ fontSize: 13, color: '#6B7A99', flex: 1 }}>{cat}</span>
                  <span style={{ fontSize: 11, color: '#6B7A99', background: '#F0F4FF', borderRadius: 99, padding: '1px 7px' }}>
                    {cat === 'All Printers' ? products.length : products.filter((p) => p.category === cat).length}
                  </span>
                </label>
              ))}

              <div className="sidebar-section">
                <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 14, color: '#1A2235', marginBottom: 12 }}>💰 Price Range</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                  <input
                    type="number"
                    value={minPrice}
                    onChange={(e) => setMinPrice(Number(e.target.value))}
                    style={{ width: 60, border: '1.5px solid #E2E8F4', borderRadius: 8, padding: '6px 10px', fontSize: 13, outline: 'none' }}
                  />
                  <span style={{ color: '#6B7A99' }}>–</span>
                  <input
                    type="number"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(Number(e.target.value))}
                    style={{ width: 60, border: '1.5px solid #E2E8F4', borderRadius: 8, padding: '6px 10px', fontSize: 13, outline: 'none' }}
                  />
                </div>
                <button
                  style={{ background: '#005CBB', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 0', width: '100%', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => {}}
                >
                  Apply Filter
                </button>
              </div>

              <div className="sidebar-section">
                <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 14, color: '#1A2235', marginBottom: 12 }}>⭐ Minimum Rating</div>
                {[{ label: 'All Ratings', val: 0 }, { label: '4★ & above', val: 4 }, { label: '5★ Only', val: 5 }].map((r) => (
                  <label key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="rating"
                      checked={minRating === r.val}
                      onChange={() => setMinRating(r.val)}
                      style={{ accentColor: '#005CBB' }}
                    />
                    <span style={{ fontSize: 13, color: '#6B7A99' }}>{r.label}</span>
                  </label>
                ))}
              </div>

              <div className="sidebar-section">
                <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 14, color: '#1A2235', marginBottom: 12 }}>🏷️ Special Offers</div>
                {[
                  { label: 'On Sale',      val: onSale,       set: setOnSale },
                  { label: 'New Arrivals', val: newArrivals,  set: setNewArrivals },
                  { label: 'Best Sellers', val: bestSellers,  set: setBestSellers },
                ].map((o) => (
                  <label key={o.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={o.val}
                      onChange={() => o.set(!o.val)}
                      style={{ accentColor: '#005CBB', width: 15, height: 15 }}
                    />
                    <span style={{ fontSize: 13, color: '#6B7A99' }}>{o.label}</span>
                  </label>
                ))}
              </div>
            </aside>

            {/* Products */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <div style={{ fontSize: 14, color: '#6B7A99' }}>Showing <strong style={{ color: '#1A2235' }}>{filtered.length}</strong> HP Printers</div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1.5px solid #E2E8F4', borderRadius: 10, padding: '8px 14px', flex: 1, maxWidth: 360 }}>
                    <span style={{ color: '#6B7A99' }}>🔍</span>
                    <input
                      type="text"
                      placeholder="Search printers..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ border: 'none', outline: 'none', fontSize: 14, background: 'transparent', width: 160, fontFamily: "'DM Sans',sans-serif" }}
                    />
                  </div>
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    style={{ border: '1.5px solid #E2E8F4', borderRadius: 8, padding: '8px 14px', fontSize: 13, background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'DM Sans',sans-serif" }}
                  >
                    <option value="default">Sort: Featured</option>
                    <option value="price-asc">Price: Low to High</option>
                    <option value="price-desc">Price: High to Low</option>
                    <option value="rating">Top Rated</option>
                  </select>
                </div>
              </div>

              {filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#6B7A99' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
                  <p style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1A2235', marginBottom: '.5rem' }}>No printers found</p>
                  <p>Try adjusting your filters or search term.</p>
                </div>
              ) : (
                <div className="products-grid">
                  {filtered.map((product) => (
                    <div key={product.id} className="product-card">
                      <div className="product-img-wrap">
                        {product.badge && (
                          <span style={{
                            position: 'absolute', top: 12, left: 12,
                            background: badgeColors[product.badgeClass] || '#005CBB',
                            color: '#fff', fontSize: 11, fontWeight: 700,
                            padding: '3px 10px', borderRadius: 6,
                          }}>{product.badge}</span>
                        )}
                        {productImages[product.id] && (
                          <img
                            src={productImages[product.id]}
                            alt={product.name}
                            className="product-img"
                          />
                        )}
                      </div>
                      <div style={{ padding: '1.25rem' }}>
                        <div style={{ fontSize: 11, color: '#005CBB', fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 4 }}>{product.category}</div>
                        <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 15, color: '#1A2235', marginBottom: 6, lineHeight: 1.3 }}>{product.name}</div>
                        <div style={{ fontSize: 12, color: '#6B7A99', lineHeight: 1.6, marginBottom: 12 }}>{product.desc}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 12 }}>
                          <span style={{ color: '#FFB800', fontSize: 12 }}>{'★'.repeat(product.rating)}{'☆'.repeat(5 - product.rating)}</span>
                          <span style={{ fontSize: 12, color: '#6B7A99' }}>({product.reviews})</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div>
                            <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 20, color: '#1A2235' }}>{product.price}</span>
                            <span style={{ fontSize: 13, color: '#6B7A99', textDecoration: 'line-through', marginLeft: 6 }}>{product.oldPrice}</span>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); addToCart(product); }}
                            style={{
                              background: addedIds[product.id] ? '#00A86B' : '#005CBB',
                              color: '#fff', border: 'none', cursor: 'pointer',
                              width: 38, height: 38, borderRadius: 10,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 18, transition: 'background .2s, transform .15s',
                              transform: addedIds[product.id] ? 'scale(1.15)' : 'scale(1)',
                            }}
                          >
                            {addedIds[product.id] ? '✓' : '+'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── WHY SECTION ── */}
        <section style={{ background: '#0A0F1E', padding: '64px 2rem' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 48, alignItems: 'center' }}>
            <div>
              <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 'clamp(2rem,3.5vw,3rem)', color: '#fff', lineHeight: 1.15, marginBottom: '1rem' }}>
                Why Choose <span style={{ color: '#FF6B00' }}>Spectra Computech?</span>
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.8, fontSize: '1rem', marginBottom: '2rem' }}>
                Your trusted global HP printer destination. Genuine products, expert support, and unbeatable prices.
              </p>
              <Link href="/shop" style={{ background: '#FF6B00', color: '#fff', padding: '14px 32px', borderRadius: 10, fontWeight: 700, fontSize: 15, textDecoration: 'none', display: 'inline-block' }}>Shop Now →</Link>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {[
                { icon: '🌍', title: 'Worldwide Delivery',    desc: '150+ countries served' },
                { icon: '🏅', title: 'Authorized Reseller',   desc: '100% genuine products' },
                { icon: '🔒', title: 'Secure Checkout',       desc: 'SSL encrypted payments' },
                { icon: '↩️', title: '15-Day Returns',        desc: 'Hassle-free policy' },
              ].map((item) => (
                <div key={item.title} className="why-card">
                  <div style={{ width: 46, height: 46, background: 'rgba(0,92,187,0.25)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: '1rem' }}>{item.icon}</div>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 14, color: '#fff', marginBottom: 5 }}>{item.title}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />

        {/* ── MODAL 1: Enter Model ── */}
        <div className={`dv-overlay${modal === 'modal1' ? ' open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) closeAll(); }}>
          <div className="dv-modal">
            <button
              style={{ position: 'absolute', top: 14, right: 18, background: 'none', border: 'none', fontSize: 20, color: '#888', cursor: 'pointer', lineHeight: 1 }}
              onClick={closeAll}
            >✕</button>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16, minHeight: 64, alignItems: 'center' }}>
              {ModalLogos[currentBrand]}
            </div>
            <div className="dv-title">Quick Download Free Drivers</div>
            <label className="dv-label" htmlFor="printer-model">
              Enter <span style={{ fontWeight: 800 }}>{currentBrand}</span> Printer Model:
            </label>
            <input
              className="dv-input"
              id="printer-model"
              type="text"
              placeholder={brandPlaceholders[currentBrand] || `e.g., ${currentBrand} Printer`}
              value={printerModel}
              onChange={(e) => setPrinterModel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') startDownload(); }}
            />
            <button className="dv-dl-btn" onClick={startDownload}>Start Download</button>
          </div>
        </div>

        {/* ── MODAL 2: Download + Install Progress ── */}
        <div className={`dv-overlay${modal === 'modal2' ? ' open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) closeAll(); }}>
          <div className="dv-modal">
            <button
              style={{ position: 'absolute', top: 14, right: 18, background: 'none', border: 'none', fontSize: 20, color: '#888', cursor: 'pointer', lineHeight: 1 }}
              onClick={closeAll}
            >✕</button>
            <div className="dv-title">Downloading Driver</div>
            <p className="dv-model-line">Model: <span style={{ fontWeight: 700 }}>{modelDisplay}</span></p>

            {/* Download bar */}
            <div className="dv-bar-section">
              <p className="dv-progress-label" style={{ color: dlDone ? '#00A86B' : '#333' }}>
                {dlDone ? 'Download Complete ✓' : 'Downloading...'}
              </p>
              <div className="dv-progress-track">
                <div className="dv-progress-bar" style={{ width: `${dlPct}%` }}>
                  <span className="dv-progress-pct">{dlPct}%</span>
                </div>
              </div>
            </div>

            {/* Install bar */}
            <div className={`dv-bar-section${!instStarted ? ' disabled' : ''}`} style={{ marginTop: 20 }}>
              <p className="dv-progress-label" style={{ color: instFailed ? '#cc0000' : instStarted ? '#333' : '#b0b8c8' }}>
                {instFailed ? 'Installation Failed ✗' : 'Installing...'}
              </p>
              <div className="dv-progress-track">
                <div
                  className={`dv-install-bar${instFailed ? ' error' : ''}`}
                  style={{ width: instStarted ? `${instPct}%` : '0%', minWidth: instStarted && instPct > 0 ? 44 : 0 }}
                >
                  {instStarted && <span className="dv-progress-pct">{instPct}%</span>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── MODAL 3: Installation Error ── */}
        <div className={`dv-overlay${modal === 'modal3' ? ' open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) closeAll(); }}>
          <div className="dv-modal" style={{ textAlign: 'center', padding: '44px 36px 40px' }}>
            <button
              style={{ position: 'absolute', top: 14, right: 18, background: 'none', border: 'none', fontSize: 20, color: '#888', cursor: 'pointer', lineHeight: 1 }}
              onClick={closeAll}
            >✕</button>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <svg viewBox="0 0 90 80" width="88" height="78" fill="none" xmlns="http://www.w3.org/2000/svg">
                <polygon points="45,5 85,75 5,75" fill="#FFD600" stroke="#e6b800" strokeWidth="2.5" />
                <text x="45" y="66" textAnchor="middle" fontFamily="Arial Black,sans-serif" fontWeight="900" fontSize="36" fill="#222">!</text>
              </svg>
            </div>

            <div className="dv-error-title">Installation Error</div>
            <p className="dv-error-desc">
              Please do not take any Action; otherwise your Warranty and<br />Hardware coverage will be void.
            </p>

            {/* Static yellow alert bar */}
            <div style={{ background: '#FFD600', borderRadius: 10, padding: '15px 24px', border: '2px solid #e6b800', boxShadow: '0 4px 20px rgba(230,184,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>⚠</span>
              <span style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 800, fontSize: 15.5, color: '#5a3a00' }}>Please Contact Our Chat Team</span>
            </div>
          </div>
        </div>

        {/* ── TOAST ── */}
        {toast && (
          <div className="sc-toast">
            <span style={{ fontSize: 22, flexShrink: 0 }}>🛒</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#fff', marginBottom: 2 }}>Added to Cart!</div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {toast.name} — <strong style={{ color: '#FF6B00' }}>{toast.price}</strong>
              </div>
            </div>
            <div style={{ background: '#005CBB', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
              🛍 {cartCount} item{cartCount !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>
    </>
  );
}